/**
 * The airship's entries in the prefab catalog (ADR 0033): the ids content
 * places (`scenePrefabIds`), each a `ShipPrefab` built with the ship's kit
 * (`types.ts`). The exterior is a world-coordinate set piece placed at the
 * origin; the mooring post and the cargo stand at their placement; the
 * clutter cards of a scene are drawn together as one mesh by the ship world
 * (`clutter.ts`), so a clutter placement builds nothing itself; the rooms are
 * placed by the room grid, a type's level naming its prefab
 * (`rules/ship_rooms.yaml`): a level 2 room is the same builder with its
 * level (the prototype's `room.level > 1` trim and capacity).
 */
import * as THREE from 'three';
import { buildCockpit } from './cockpit.ts';
import { buildExterior } from './exterior.ts';
import { buildFarm } from './farm.ts';
import { buildHatchery } from './hatchery.ts';
import { buildCargo, buildMooringPost } from './props.ts';
import type { ShipPlace, ShipPrefab } from './types.ts';

const num = (params: Readonly<Record<string, number | boolean | string>>, key: string, fallback: number): number => {
  const v = params[key];
  return typeof v === 'number' ? v : fallback;
};

const roomOf = (place: ShipPlace, id: string): NonNullable<ShipPlace['room']> => {
  if (place.room === null) throw new Error(`${id} is a room prefab: the room grid places it`);
  return place.room;
};

const exterior: ShipPrefab = { id: 'airship_exterior', ship: true, build: (kit, _params, place) => ({ root: buildExterior(kit, place.area) }) };
const mooringPost: ShipPrefab = { id: 'ship_mooring_post', ship: true, build: (kit, params) => ({ root: buildMooringPost(kit, { w: num(params, 'w', 1), d: num(params, 'd', 1), h: num(params, 'h', 3.51) }) }) };
const cargo: ShipPrefab = { id: 'ship_cargo', ship: true, build: (kit, params) => ({ root: buildCargo(kit, { w: num(params, 'w', 2), d: num(params, 'd', 2) }) }) };
const clutter: ShipPrefab = { id: 'ship_clutter', ship: true, build: () => ({ root: new THREE.Group() }) };
const cockpit = (id: string): ShipPrefab => ({ id, ship: true, build: (kit, _params, place) => ({ root: buildCockpit(kit, roomOf(place, id)) }) });
const hatchery = (id: string): ShipPrefab => ({ id, ship: true, build: (kit, _params, place) => buildHatchery(kit, roomOf(place, id)) });
const farm = (id: string): ShipPrefab => ({ id, ship: true, build: (kit, _params, place) => buildFarm(kit, roomOf(place, id)) });

export const shipPrefabs = {
  airship_exterior: exterior,
  ship_mooring_post: mooringPost,
  ship_cargo: cargo,
  ship_clutter: clutter,
  ship_cockpit_1: cockpit('ship_cockpit_1'),
  ship_cockpit_2: cockpit('ship_cockpit_2'),
  ship_hatchery_1: hatchery('ship_hatchery_1'),
  ship_hatchery_2: hatchery('ship_hatchery_2'),
  ship_farm_1: farm('ship_farm_1'),
  ship_farm_2: farm('ship_farm_2'),
} as const;
